import { isEmpty } from 'lodash/fp';
import { ChangeEvent } from 'react';
import { FormatOptionLabelMeta } from 'react-select';

import ControlPanelContainer from 'dashboard/apps/DashboardLayoutEditor/ControlPanel/ControlPanelContainer';
import ControlPanelDivider from 'dashboard/apps/DashboardLayoutEditor/ControlPanel/ControlPanelDivider';
import ReactSelect, { OptionType } from 'dashboard/components/ReactSelect';
import { Workflow } from 'dashboard/models/workflow';
import { ModuleButton } from 'dashboard/modules/Button';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { Heading, Label, Paragraph } from 'design-system/components/Text';

type Variant = 'basic' | 'primary' | 'danger';

const VARIANT_LABEL = {
  basic: 'Default',
  primary: 'Primary',
  danger: 'Critical',
};

const VARIANT_HELPER_TEXT = {
  basic: 'The most common button style',
  primary: 'To highlight important actions',
  danger: 'For actions that will delete data or be difficult to recover from',
};

export interface ButtonPanelProps {
  module: ModuleButton;
  actionWorkflows: Workflow[];
  updateModule: (module: ModuleButton) => void;
}

const ButtonPanel = ({ module, actionWorkflows, updateModule }: ButtonPanelProps) => {
  const caseActionOptions = actionWorkflows.map((action) => ({
    label: action.attributes.name,
    value: action.id,
  }));

  const updateButtonName = (e: ChangeEvent<HTMLInputElement>) => {
    updateModule({ ...module, name: e.target.value });
  };

  const updateActionId = (value: string) => {
    updateModule({
      ...module,
      'action-id': value,
    });
  };

  const variant = module.variant || 'primary';

  const updateVariant = (value: Variant) => {
    updateModule({
      ...module,
      variant: value,
    });
  };

  return (
    <>
      <ControlPanelContainer>
        <Heading size='sm'>Settings</Heading>
        <Spacer height={3} />
        <Label size='md' weight='bold' id='button-panel__name--label'>
          Name
        </Label>
        <Input
          id='button-panel__name'
          aria-labelledby='button-panel__name--label'
          type='text'
          value={module.name}
          onChange={updateButtonName}
        />
        <Spacer height={2} />
        <Label size='md' weight='bold' id='button-panel__action--label'>
          Action
        </Label>
        <ReactSelect
          id='button-panel__input--actionId'
          aria-labelledby='button-panel__action--label'
          name='action-id'
          value={module['action-id'] || ''}
          hasError={isEmpty(module['action-id'])}
          options={caseActionOptions}
          onChange={updateActionId}
          portal={true}
        />
      </ControlPanelContainer>
      <ControlPanelDivider full />
      <ControlPanelContainer>
        <Heading size='sm'>Styling</Heading>
        <Spacer height={3} />
        <Label size='md' weight='bold' id='button-panel__button-color--label'>
          Button color
        </Label>
        <ReactSelect
          id='button-panel__button-variant'
          aria-labelledby='button-panel__button-color--label'
          options={[
            { label: VARIANT_LABEL['basic'], value: 'basic' },
            { label: VARIANT_LABEL['primary'], value: 'primary' },
            { label: VARIANT_LABEL['danger'], value: 'danger' },
          ]}
          value={variant}
          onChange={updateVariant}
          formatOptionLabel={formatVariantOption}
          portal
        />
      </ControlPanelContainer>
    </>
  );
};

const formatVariantOption = (
  { value }: { value: Variant },
  { context }: FormatOptionLabelMeta<OptionType<Variant>>,
) => {
  if (context === 'menu') {
    return (
      <div>
        <Paragraph style={{ lineBreak: 'anywhere' }}>{VARIANT_LABEL[value]}</Paragraph>
        <Paragraph size='sm'>{VARIANT_HELPER_TEXT[value]}</Paragraph>
      </div>
    );
  } else {
    return VARIANT_LABEL[value];
  }
};

export default ButtonPanel;
